import React from "react";
import { NavLink } from "react-router-dom";
import {
    LayoutDashboard, BarChart3, ShieldAlert, Globe, Cpu, LogOut,
    ChevronLeft, ChevronRight, ShieldCheck,
    Bot, Sparkles, Fingerprint, Shield, Target, Radar, Ticket,
    Network, Radio, Zap, Activity, Search as SearchIcon, Layers,
    FileCode, SlidersHorizontal, Play, GitBranch, Sliders, BookOpen, Database, FileSpreadsheet, Mail, KeyRound, Bug, FileSearch, Award, Monitor
} from "lucide-react";

export default function Sidebar({ collapsed = false, onToggle, onHoverStart, onHoverEnd, onOpenCopilot, currentUser }) {
    const handleLogout = () => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
    };

    const linkBase = "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-150 group relative";
    const linkAction = ({ isActive }) =>
        `${linkBase} ${isActive
            ? "bg-blue-500/10 text-blue-400"
            : "text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"}`;

    const SectionLabel = ({ children }) => !collapsed ? (
        <div className="px-3 pt-5 pb-1.5">
            <span className="text-[10px] font-semibold text-slate-600 uppercase tracking-[0.12em]">{children}</span>
        </div>
    ) : <div className="my-2 mx-4 h-px bg-white/[0.04]" />;

    return (
        <aside
            className={`fixed top-0 left-0 h-screen ${collapsed ? "w-[68px]" : "w-[240px]"} bg-[#0B1120] z-50 flex flex-col transition-all duration-300 border-r border-white/[0.06]`}
            onMouseEnter={onHoverStart}
            onMouseLeave={onHoverEnd}
        >
            {/* Logo */}
            <div className="h-14 flex items-center px-4 border-b border-white/[0.06]">
                {!collapsed ? (
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                            <Zap className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-[15px] font-semibold text-white tracking-tight">Sentinel</span>
                    </div>
                ) : (
                    <div className="w-full flex justify-center">
                        <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                            <Zap className="w-4 h-4 text-white" />
                        </div>
                    </div>
                )}
            </div>

            <nav className="flex-1 px-2 pt-2 overflow-y-auto custom-scrollbar space-y-0.5">
                <SectionLabel>Command</SectionLabel>

                <NavLink to="/" className={linkAction} end>
                    <Activity className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span className="text-[13px] font-medium">Pulse</span>}
                </NavLink>

                <NavLink to="/event-monitor" className={linkAction}>
                    <Radar className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Live Monitor</span>
                            <span className="text-[8px] font-semibold bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">LIVE</span>
                        </div>
                    )}
                </NavLink>

                <SectionLabel>Triage & Forensics</SectionLabel>

                <NavLink to="/incidents" className={linkAction}>
                    <ShieldAlert className="w-[18px] h-[18px] flex-shrink-0 text-red-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">All Incidents</span>
                            <span className="text-[8px] font-semibold bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">11k+</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/cases" className={linkAction}>
                    <Ticket className="w-[18px] h-[18px] flex-shrink-0 text-amber-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Cases Board</span>
                            <span className="text-[8px] font-semibold bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">TRIAGE</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/investigations" className={linkAction}>
                    <Layers className="w-[18px] h-[18px] flex-shrink-0 text-indigo-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">AI Investigations</span>
                            <span className="text-[8px] font-semibold bg-indigo-500/20 text-indigo-400 px-1.5 py-0.5 rounded">FORENSICS</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/attack-story" className={linkAction}>
                    <GitBranch className="w-[18px] h-[18px] flex-shrink-0 text-purple-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Attack Stories</span>
                            <span className="text-[8px] font-semibold bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">KILL-CHAIN</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/response-actions" className={linkAction}>
                    <Play className="w-[18px] h-[18px] flex-shrink-0 text-rose-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Response Actions</span>
                            <span className="text-[8px] font-semibold bg-rose-500/20 text-rose-400 px-1.5 py-0.5 rounded">AUTO</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/approvals" className={linkAction}>
                    <ShieldCheck className="w-[18px] h-[18px] flex-shrink-0 text-amber-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Firewall Approvals</span>
                            <span className="text-[8px] font-semibold bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">QUEUE</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/workflows" className={linkAction}>
                    <GitBranch className="w-[18px] h-[18px] flex-shrink-0 text-purple-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Workflows</span>
                            <span className="text-[8px] font-semibold bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">SOAR</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/workflows/designer" className={linkAction}>
                    <Sliders className="w-[18px] h-[18px] flex-shrink-0 text-fuchsia-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Designer</span>
                            <span className="text-[8px] font-semibold bg-fuchsia-500/20 text-fuchsia-400 px-1.5 py-0.5 rounded">STUDIO</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/runbooks" className={linkAction}>
                    <BookOpen className="w-[18px] h-[18px] flex-shrink-0 text-emerald-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Runbooks</span>
                            <span className="text-[8px] font-semibold bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">SOP</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/skills" className={linkAction}>
                    <Sparkles className="w-[18px] h-[18px] flex-shrink-0 text-purple-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Skill Library</span>
                            <span className="text-[8px] font-semibold bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">AI</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/insights" className={linkAction}>
                    <Database className="w-[18px] h-[18px] flex-shrink-0 text-cyan-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Enterprise Insights</span>
                            <span className="text-[8px] font-semibold bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded">FACTS</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/posture" className={linkAction}>
                    <ShieldCheck className="w-[18px] h-[18px] flex-shrink-0 text-orange-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Posture Score</span>
                            <span className="text-[8px] font-semibold bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded">CSPM</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/event-explorer" className={linkAction}>
                    <SearchIcon className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span className="text-[13px] font-medium">Event Explorer</span>}
                </NavLink>

                <NavLink to="/analysis" className={linkAction}>
                    <Cpu className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span className="text-[13px] font-medium">Analysis</span>}
                </NavLink>

                <SectionLabel>Detect</SectionLabel>

                <NavLink to="/detection-intelligence" className={linkAction}>
                    <Activity className="w-[18px] h-[18px] flex-shrink-0 text-blue-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Detection Intel</span>
                            <span className="text-[8px] font-semibold bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">QueryTel</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/detection-coverage" className={linkAction}>
                    <Target className="w-[18px] h-[18px] flex-shrink-0 text-emerald-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Detection Coverage</span>
                            <span className="text-[8px] font-semibold bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">MITRE</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/detection-rules" className={linkAction}>
                    <FileCode className="w-[18px] h-[18px] flex-shrink-0 text-purple-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Detection Rules</span>
                            <span className="text-[8px] font-semibold bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">RULES</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/detection-routing" className={linkAction}>
                    <SlidersHorizontal className="w-[18px] h-[18px] flex-shrink-0 text-cyan-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Detection Routing</span>
                            <span className="text-[8px] font-semibold bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded">AUTO</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/network-traffic" className={linkAction}>
                    <Network className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span className="text-[13px] font-medium">Network Traffic</span>}
                </NavLink>

                <NavLink to="/early-warning" className={linkAction}>
                    <Radio className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span className="text-[13px] font-medium">Early Warning</span>}
                </NavLink>

                <NavLink to="/ai-threat-insights" className={linkAction}>
                    <ShieldCheck className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span className="text-[13px] font-medium">AI Insights</span>}
                </NavLink>

                <SectionLabel>Hunt</SectionLabel>

                <NavLink to="/darkweb-monitor" className={linkAction}>
                    <ShieldAlert className="w-[18px] h-[18px] flex-shrink-0 text-rose-400" />
                    {!collapsed && <span className="text-[13px] font-medium">Dark Web</span>}
                </NavLink>

                <NavLink to="/threat-hunt" className={linkAction}>
                    <Target className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Threat Hunt</span>
                            <span className="text-[8px] font-semibold bg-violet-500/20 text-violet-400 px-1.5 py-0.5 rounded">AI</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/threat-intel" className={linkAction}>
                    <Shield className="w-[18px] h-[18px] flex-shrink-0 text-orange-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[13px] font-medium">Threat Intel</span>
                            <span className="text-[8px] font-semibold bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded">AUTO</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/global-map" className={linkAction}>
                    <Globe className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span className="text-[13px] font-medium">Threat Map</span>}
                </NavLink>

                <NavLink to="/mitre-coverage" className={linkAction}>
                    <Target className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span className="text-[13px] font-medium">MITRE Coverage</span>}
                </NavLink>

                <SectionLabel>Assets</SectionLabel>

                <NavLink to="/asset-identity" className={linkAction}>
                    <Fingerprint className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span className="text-[13px] font-medium">Asset Identity</span>}
                </NavLink>

                <NavLink to="/threatdown" className={linkAction}>
                    <ShieldCheck className="w-[18px] h-[18px] flex-shrink-0 text-blue-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between flex-1">
                            <span className="text-[13px] font-medium">ThreatDown EDR</span>
                            <span className="text-[8px] font-semibold bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">12 SITES</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/defender" className={linkAction}>
                    <Shield className="w-[18px] h-[18px] flex-shrink-0 text-sky-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between flex-1">
                            <span className="text-[13px] font-medium">MS Defender XDR</span>
                            <span className="text-[8px] font-semibold bg-sky-500/20 text-sky-400 px-1.5 py-0.5 rounded">AZURE</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/email-security" className={linkAction}>
                    <Mail className="w-[18px] h-[18px] flex-shrink-0 text-purple-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between flex-1">
                            <span className="text-[13px] font-medium">ThreatGate MXDR</span>
                            <span className="text-[8px] font-semibold bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">EMAIL</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/identity-protection" className={linkAction}>
                    <KeyRound className="w-[18px] h-[18px] flex-shrink-0 text-fuchsia-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between flex-1">
                            <span className="text-[13px] font-medium">Entra ID Protection</span>
                            <span className="text-[8px] font-semibold bg-fuchsia-500/20 text-fuchsia-400 px-1.5 py-0.5 rounded">IDENTITY</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/tvm" className={linkAction}>
                    <Bug className="w-[18px] h-[18px] flex-shrink-0 text-amber-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between flex-1">
                            <span className="text-[13px] font-medium">Defender TVM</span>
                            <span className="text-[8px] font-semibold bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">CVEs</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/purview" className={linkAction}>
                    <FileSearch className="w-[18px] h-[18px] flex-shrink-0 text-teal-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between flex-1">
                            <span className="text-[13px] font-medium">Purview Audit Log</span>
                            <span className="text-[8px] font-semibold bg-teal-500/20 text-teal-400 px-1.5 py-0.5 rounded">PURVIEW</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/attack-surface" className={linkAction}>
                    <Globe className="w-[18px] h-[18px] flex-shrink-0 text-cyan-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between flex-1">
                            <span className="text-[13px] font-medium">Attack Surface</span>
                            <span className="text-[8px] font-semibold bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded">EASM</span>
                        </div>
                    )}
                </NavLink>

                {/* AI Copilot */}
                <div className="pt-3">
                    <button
                        onClick={onOpenCopilot}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-blue-400 hover:bg-blue-500/10 transition-all"
                    >
                        <div className="relative">
                            <Bot className="w-[18px] h-[18px]" />
                            <Sparkles className="absolute -top-1 -right-1 w-2 h-2 text-blue-300 animate-pulse" />
                        </div>
                        {!collapsed && <span className="text-[13px] font-medium">AI Copilot</span>}
                    </button>
                </div>

                <SectionLabel>Reports & Audits</SectionLabel>

                <NavLink to="/reports" className={linkAction}>
                    <FileSpreadsheet className="w-[18px] h-[18px] flex-shrink-0 text-sky-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between flex-1">
                            <span className="text-[13px] font-medium">Reports Center</span>
                            <span className="text-[8px] font-semibold bg-sky-500/20 text-sky-400 px-1.5 py-0.5 rounded">STUDIO</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/executive-brief" className={linkAction}>
                    <Award className="w-[18px] h-[18px] flex-shrink-0 text-indigo-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between flex-1">
                            <span className="text-[13px] font-medium">Executive Brief</span>
                            <span className="text-[8px] font-semibold bg-indigo-500/20 text-indigo-400 px-1.5 py-0.5 rounded">BOARD PDF</span>
                        </div>
                    )}
                </NavLink>

                <SectionLabel>Infrastructure</SectionLabel>

                <NavLink to="/monitoring" className={linkAction}>
                    <Monitor className="w-[18px] h-[18px] flex-shrink-0 text-emerald-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between flex-1">
                            <span className="text-[13px] font-medium">Monitoring</span>
                            <span className="text-[8px] font-semibold bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">LIVE</span>
                        </div>
                    )}
                </NavLink>

                <NavLink to="/machines" className={linkAction}>
                    <Cpu className="w-[18px] h-[18px] flex-shrink-0 text-cyan-400" />
                    {!collapsed && (
                        <div className="flex items-center justify-between flex-1">
                            <span className="text-[13px] font-medium">Machine Connector</span>
                            <span className="text-[8px] font-semibold bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded">REMOTE</span>
                        </div>
                    )}
                </NavLink>

                <SectionLabel>Logs</SectionLabel>

                <NavLink to="/logs/live" className={linkAction}>
                    <Radio className="w-[18px] h-[18px] flex-shrink-0 text-emerald-400 animate-pulse" />
                    {!collapsed && (
                        <div className="flex items-center justify-between flex-1">
                            <span className="text-[13px] font-medium">Live Logs</span>
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        </div>
                    )}
                </NavLink>
            </nav>

            {/* Footer */}
            <div className="p-2 border-t border-white/[0.04]">
                {!collapsed && (
                    <div className="flex items-center gap-2.5 px-2.5 py-2 mb-2 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                        <div className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0 shadow-sm">
                            {currentUser?.name ? currentUser.name.split(" ").filter(Boolean).map(n => n[0].toUpperCase()).slice(0, 2).join("") : (localStorage.getItem("userName")?.split(" ").filter(Boolean).map(n => n[0].toUpperCase()).slice(0, 2).join("") || "SA")}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-semibold text-slate-200 truncate">
                                {currentUser?.name || localStorage.getItem("userName") || "Security Analyst"}
                            </p>
                            <p className="text-[10px] text-slate-500 font-mono truncate">
                                {currentUser?.email || localStorage.getItem("userEmail") || "analyst@querytel.com"}
                            </p>
                        </div>
                    </div>
                )}
                <button onClick={onToggle} className="w-full flex items-center justify-center p-2 rounded-lg text-slate-600 hover:text-slate-400 hover:bg-white/[0.04] transition-colors mb-1">
                    {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                </button>
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:text-rose-400 hover:bg-rose-500/10 transition-all">
                    <LogOut className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span className="text-[13px] font-medium">Sign Out</span>}
                </button>
            </div>
        </aside>
    );
}
